"use client"
import { useRef, useEffect, useState } from "react"
import {
  Trash2,
  Search,
  Folder,
  ChevronDown,
  ChevronRight,
  BookOpen,
  Filter,
  X,
  Loader,
  AlertCircle,
  FolderOpen,
  FileText,
  ArrowUpRight,
  Info,
  XCircle,
  ArrowLeft,
  Calendar,
  List,
  Grid,
  Plus,
  ArrowUpDown,
  MessageCircle,
  GitBranch,
  Network,
  Bot,
  PenTool,
  Save,
} from "lucide-react"
import Flashcard from "./FlashCard"
import ReactMarkdown from "react-markdown"
import { BlockMath, InlineMath } from "react-katex"
import "katex/dist/katex.min.css"
import FlowChart from "./FlowChart"
import MindMap from "./MindMap"
import { Excalidraw } from "@excalidraw/excalidraw"
import { getAllnotes } from "./api"
import MnemonicDevice from "./MnemonicDevice"
import { postAws } from "../aws"
import { generatePresignedUrl, postStudentExcallyNotes } from "../postFileAws"
import { v4 as uuidv4 } from "uuid"

const Notes = () => {
  const [searchTerm, setSearchTerm] = useState("")
  const [notes, setNotes] = useState([])
  const [notesFetched, setNotesFetched] = useState(false)
  const [organizedNotes, setOrganizedNotes] = useState({})
  const [expandedFolder, setExpandedFolder] = useState(null)
  const [copyObj, setCopyObj] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showFilters, setShowFilters] = useState(false)
  const [activeFilter, setActiveFilter] = useState("all")
  const [sortOrder, setSortOrder] = useState("newest")
  const [favoriteNotes, setFavoriteNotes] = useState([])
  const [showMobileSearch, setShowMobileSearch] = useState(false)
  const [fullScreenNote, setFullScreenNote] = useState(null)
  const [viewMode, setViewMode] = useState("list")
  const searchInputRef = useRef(null)
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false)
  const [newNoteData, setNewNoteData] = useState({ folderName: "", topic: "" })
  const [showOptionsForPage, setShowOptionsForPage] = useState(null)
  const [fullscreenContent, setFullscreenContent] = useState(null)
  const [showFilterDropdown, setShowFilterDropdown] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [savingStates, setSavingStates] = useState({})
  const [loadingStates, setLoadingStates] = useState({})
  const [isAddFolderNoteModalOpen, setIsAddFolderNoteModalOpen] = useState(false)
  const [selectedFolder, setSelectedFolder] = useState("")
  const [excalidrawFullscreen, setExcalidrawFullscreen] = useState(null)

  // Unified excalidraw refs - use single structure for both normal and fullscreen
  const excalidrawRefs = useRef({})

  // Save Excalidraw note function - unified for both normal and fullscreen
  const saveExcalidrawNote = async (noteId, pageIndex, side, refId, noteTitle) => {
    const excalidrawAPI = excalidrawRefs.current[refId]
    if (!excalidrawAPI) {
      console.error("Excalidraw API not found for refId:", refId)
      return
    }

    try {
      setSavingStates((prev) => ({ ...prev, [refId]: true }))
      const elements = excalidrawAPI.getSceneElements()
      const appState = excalidrawAPI.getAppState()

      const excalidrawData = {
        type: "excalidraw",
        version: 2,
        source: "https://excalidraw.com",
        elements: elements,
        appState: {
          gridSize: appState.gridSize,
          viewBackgroundColor: appState.viewBackgroundColor,
          currentItemStrokeColor: appState.currentItemStrokeColor,
          currentItemBackgroundColor: appState.currentItemBackgroundColor,
          currentItemFillStyle: appState.currentItemFillStyle,
          currentItemStrokeWidth: appState.currentItemStrokeWidth,
          currentItemStrokeStyle: appState.currentItemStrokeStyle,
          currentItemRoughness: appState.currentItemRoughness,
          currentItemOpacity: appState.currentItemOpacity,
          currentItemFontFamily: appState.currentItemFontFamily,
          currentItemFontSize: appState.currentItemFontSize,
          currentItemTextAlign: appState.currentItemTextAlign,
          currentItemStartArrowhead: appState.currentItemStartArrowhead,
          currentItemEndArrowhead: appState.currentItemEndArrowhead,
          scrollX: appState.scrollX,
          scrollY: appState.scrollY,
          zoom: appState.zoom,
          currentItemRoundness: appState.currentItemRoundness,
          gridColor: appState.gridColor,
          currentStrokeOptions: appState.currentStrokeOptions,
          previousGridSize: appState.previousGridSize,
          frameRendering: appState.frameRendering,
        },
        files: {},
      }

      // Convert to JSON and create file - use noteTitle for filename
      const jsonString = JSON.stringify(excalidrawData)
      const jsonBlob = new Blob([jsonString], { type: "application/json" })
      const fileName = `${noteTitle || refId}.json`
      const file = new File([jsonBlob], fileName, { type: "application/json" })

      // Upload to S3 using existing function
      const studentId = localStorage.getItem("studentId")
      const uploadResult = await postStudentExcallyNotes(file, studentId, fileName, refId)

      if (uploadResult && uploadResult.uri) {
        // Send S3 URI to backend to save in MongoDB
        const saveData = {
          request: "save-excalidraw-note",
          body: {
            noteId,
            pageIndex,
            side,
            s3Uri: uploadResult.uri,
          },
        }

        const backendResponse = await postAws("https://api-dev.aurait.ai/notes/updateNotes", "POST", saveData)

        if (backendResponse.statusCode === 200) {
          // Update local state
          setFullScreenNote((prevNote) => {
            if (!prevNote || prevNote._id !== noteId) return prevNote
            const updatedNote = { ...prevNote }
            const page = { ...updatedNote.noteContents[pageIndex] }
            page[side] = {
              noteType: "manual",
              noteData: uploadResult.uri,
            }
            updatedNote.noteContents[pageIndex] = page
            return updatedNote
          })

          // Update notes list
          setNotes((prevNotes) =>
            prevNotes.map((note) => {
              if (note._id === noteId) {
                const updatedNote = { ...note }
                const page = { ...updatedNote.noteContents[pageIndex] }
                page[side] = {
                  noteType: "manual",
                  noteData: uploadResult.uri,
                }
                updatedNote.noteContents[pageIndex] = page
                return updatedNote
              }
              return note
            }),
          )

          // Update fullscreen content if it's currently displayed
          setFullscreenContent((prevContent) => {
            if (
              prevContent &&
              prevContent._noteId === noteId &&
              prevContent._pageIndex === pageIndex &&
              prevContent._side === side
            ) {
              return {
                ...prevContent,
                noteData: uploadResult.uri,
              }
            }
            return prevContent
          })

          // Show success message
          const successMessage = document.createElement("div")
          successMessage.className = "fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50"
          successMessage.textContent = "Drawing saved successfully!"
          document.body.appendChild(successMessage)
          setTimeout(() => {
            if (document.body.contains(successMessage)) {
              document.body.removeChild(successMessage)
            }
          }, 3000)
        }
      }
    } catch (error) {
      console.error("Error saving Excalidraw note:", error)
      const errorMessage = document.createElement("div")
      errorMessage.className = "fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50"
      errorMessage.textContent = "Failed to save drawing. Please try again."
      document.body.appendChild(errorMessage)
      setTimeout(() => {
        if (document.body.contains(errorMessage)) {
          document.body.removeChild(errorMessage)
        }
      }, 3000)
    } finally {
      setSavingStates((prev) => ({ ...prev, [refId]: false }))
    }
  }

  // Load Excalidraw note function - unified for both normal and fullscreen
  const loadExcalidrawNote = async (s3Uri, refId) => {
    if (!s3Uri) return

    try {
      setLoadingStates((prev) => ({ ...prev, [refId]: true }))
      console.log("Loading Excalidraw note:", s3Uri, "for refId:", refId)

      // Fix the S3 URI path - ensure it starts with "students" not "tudents"
      let correctedS3Uri = s3Uri
      if (s3Uri.includes("tudents/")) {
        correctedS3Uri = s3Uri.replace("tudents/", "Students/")
      }

      // Generate presigned URL to fetch the JSON
      const presignedUrl = await generatePresignedUrl(correctedS3Uri)
      console.log("Generated presigned URL:", presignedUrl)

      if (presignedUrl) {
        const response = await fetch(presignedUrl)
        if (response.ok) {
          const excalidrawData = await response.json()
          console.log("Loaded Excalidraw data:", excalidrawData)

          // Use unified ref structure
          const excalidrawAPI = excalidrawRefs.current[refId]
          console.log("Found excalidraw API:", !!excalidrawAPI, "for refId:", refId)

          if (excalidrawAPI) {
            // Use consistent timeout for both normal and fullscreen
            setTimeout(() => {
              try {
                excalidrawAPI.updateScene({
                  elements: excalidrawData.elements || [],
                  appState: {
                    ...excalidrawData.appState,
                    // Ensure proper zoom and scroll
                    zoom: excalidrawData.appState?.zoom || { value: 1 },
                    scrollX: excalidrawData.appState?.scrollX || 0,
                    scrollY: excalidrawData.appState?.scrollY || 0,
                  },
                  files: excalidrawData.files || {},
                })
                console.log("Successfully updated scene with elements:", excalidrawData.elements?.length || 0)
              } catch (updateError) {
                console.error("Error updating Excalidraw scene:", updateError)
              }
            }, 100) // Consistent 100ms timeout
          } else {
            console.warn("Excalidraw API not found for refId:", refId)
          }
        } else {
          console.error("Failed to fetch Excalidraw data:", response.status, response.statusText)
        }
      }
    } catch (error) {
      console.error("Error loading Excalidraw note:", error)
    } finally {
      // Clear loading state after a delay to ensure smooth transition
      setTimeout(() => {
        setLoadingStates((prev) => ({ ...prev, [refId]: false }))
      }, 1000)
    }
  }

  const renderLatex = (text) => {
    if (!text) return null

    const blockLatexRegex = /\[\s*([\s\S]*?)\s*\]/g
    const inlineLatexRegex = /\$\$\s*([\s\S]*?)\s*\$\$/g

    const parts = text.split(blockLatexRegex)

    return parts.map((part, index) => {
      if (index % 2 === 1) {
        return <BlockMath key={`block-${index}`}>{part}</BlockMath>
      } else {
        const inlineParts = part.split(inlineLatexRegex)
        return inlineParts.map((inlinePart, inlineIndex) => {
          if (inlineIndex % 2 === 1) {
            return <InlineMath key={`inline-${index}-${inlineIndex}`}>{inlinePart}</InlineMath>
          } else {
            return (
              <ReactMarkdown
                key={`text-${index}-${inlineIndex}`}
                components={{
                  p: ({ children }) => <p className="mb-4">{children}</p>,
                  h1: ({ children }) => <h1 className="text-2xl font-bold mb-4">{children}</h1>,
                  h2: ({ children }) => <h2 className="text-xl font-bold mb-3">{children}</h2>,
                  h3: ({ children }) => <h3 className="text-lg font-bold mb-2">{children}</h3>,
                  ul: ({ children }) => <ul className="list-disc pl-6 mb-4">{children}</ul>,
                  li: ({ children }) => <li className="mb-1">{children}</li>,
                }}
              >
                {inlinePart}
              </ReactMarkdown>
            )
          }
        })
      }
    })
  }

  useEffect(() => {
    const getNotes = async () => {
      setIsLoading(true)
      try {
        const allNotes = await getAllnotes(localStorage.getItem("classRoomId"), localStorage.getItem("studentId"))
        const notesArray = allNotes.body.data
        setNotes([...notesArray])
        organizeNotes([...notesArray])

        const savedFavorites = localStorage.getItem("favoriteNotes")
        if (savedFavorites) {
          setFavoriteNotes(JSON.parse(savedFavorites))
        }
      } catch (err) {
        console.error("Error fetching notes:", err)
        setError("Failed to load notes. Please try again.")
      } finally {
        setIsLoading(false)
      }
    }

    if (!notesFetched) {
      getNotes()
      setNotesFetched(true)
    }
  }, [notesFetched])

  useEffect(() => {
    if (showMobileSearch && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [showMobileSearch])

  useEffect(() => {
    localStorage.setItem("favoriteNotes", JSON.stringify(favoriteNotes))
  }, [favoriteNotes])

  useEffect(() => {
    if (fullScreenNote) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "auto"
    }

    return () => {
      document.body.style.overflow = "auto"
    }
  }, [fullScreenNote])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showOptionsForPage !== null && !event.target.closest(".options-menu")) {
        setShowOptionsForPage(null)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [showOptionsForPage])

  const copyToClipboard = async (textToCopy, noteId) => {
    try {
      await navigator.clipboard.writeText(textToCopy)
      setCopyObj({
        ...copyObj,
        [noteId]: true,
      })
      setTimeout(() => {
        setCopyObj((prev) => ({
          ...prev,
          [noteId]: false,
        }))
      }, 2000)
    } catch (err) {
      console.error("Failed to copy text: ", err)
    }
  }

  const organizeNotes = (notesData) => {
    const organized = notesData.reduce((acc, note) => {
      const noteFolder = note.noteFolder || "Uncategorized"
      if (!acc[noteFolder]) {
        acc[noteFolder] = []
      }
      acc[noteFolder].push(note)
      return acc
    }, {})
    setOrganizedNotes(organized)
  }

  const toggleFolder = (noteFolder) => {
    setExpandedFolder(expandedFolder === noteFolder ? null : noteFolder)
  }

  const toggleFavorite = (noteId) => {
    setFavoriteNotes((prev) => {
      if (prev.includes(noteId)) {
        return prev.filter((id) => id !== noteId)
      } else {
        return [...prev, noteId]
      }
    })
  }

  const getFilteredNotes = () => {
    const searchFiltered = Object.entries(organizedNotes).reduce((acc, [noteFolder, folderNotes]) => {
      const filtered = folderNotes.filter(
        (note) =>
          note?.noteTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          noteFolder.toLowerCase().includes(searchTerm.toLowerCase()),
      )
      if (filtered.length > 0) {
        acc[noteFolder] = filtered
      }
      return acc
    }, {})

    if (activeFilter === "all") {
      return searchFiltered
    }

    if (activeFilter === "favorites") {
      return Object.entries(searchFiltered).reduce((acc, [noteFolder, folderNotes]) => {
        const filtered = folderNotes.filter((note) => favoriteNotes.includes(note._id))
        if (filtered.length > 0) {
          acc[noteFolder] = filtered
        }
        return acc
      }, {})
    }

    return Object.entries(searchFiltered).reduce((acc, [noteFolder, folderNotes]) => {
      const filtered = folderNotes.filter((note) => {
        if (!note || !note.noteContents || !Array.isArray(note.noteContents)) {
          return false
        }
        return note.noteContents.some((page) => {
          if (!page || !page.aSide || !page.bSide) {
            return false
          }
          return (
            (page.aSide.noteType && page.aSide.noteType.toLowerCase() === activeFilter.toLowerCase()) ||
            (page.bSide.noteType &&
              page.bSide.noteType !== "NONE" &&
              page.bSide.noteType.toLowerCase() === activeFilter.toLowerCase())
          )
        })
      })
      if (filtered.length > 0) {
        acc[noteFolder] = filtered
      }
      return acc
    }, {})
  }

  const sortNotes = (notes) => {
    return [...notes].sort((a, b) => {
      if (sortOrder === "newest") {
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      } else if (sortOrder === "oldest") {
        return new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
      } else if (sortOrder === "alphabetical") {
        return a.noteTitle.localeCompare(b.noteTitle)
      }
      return 0
    })
  }

  const getNoteTypeIcon = (type) => {
    if (!type) return <FileText className="text-gray-500" size={16} />

    const lowerType = type.toLowerCase()
    switch (lowerType) {
      case "flashcard":
        return <BookOpen className="text-orange-500" size={16} />
      case "manual":
        return <PenTool className="text-purple-500" size={16} />
      case "chat":
        return <MessageCircle className="text-blue-500" size={16} />
      case "flowchart":
        return <GitBranch className="text-green-500" size={16} />
      case "mindmap":
        return <Network className="text-pink-500" size={16} />
      default:
        return <FileText className="text-gray-500" size={16} />
    }
  }

  // Function to fetch fresh note data
  const fetchFreshNoteData = async (noteId) => {
    try {
      const response = await postAws("https://api-dev.aurait.ai/notes/updateNotes", "POST", {
        request: "get-note-by-id",
        body: {
          noteId: noteId,
        },
      })

      if (response && response.body && response.body.data) {
        const freshNote = response.body.data
        // Update the notes array with fresh data
        setNotes((prevNotes) => prevNotes.map((note) => (note._id === noteId ? freshNote : note)))

        // Update organized notes with fresh data
        setOrganizedNotes((prevOrganized) => {
          const updated = { ...prevOrganized }
          Object.keys(updated).forEach((folder) => {
            updated[folder] = updated[folder].map((note) => (note._id === noteId ? freshNote : note))
          })
          return updated
        })

        // Update fullscreen note if it's the same note
        setFullScreenNote((prevNote) => (prevNote && prevNote._id === noteId ? freshNote : prevNote))

        // Update fullscreen content if it matches
        setFullscreenContent((prevContent) => {
          if (prevContent && prevContent._noteId === noteId) {
            const freshContent = freshNote.noteContents[prevContent._pageIndex][prevContent._side]
            return {
              ...freshContent,
              _noteId: noteId,
              _pageIndex: prevContent._pageIndex,
              _side: prevContent._side,
            }
          }
          return prevContent
        })

        return freshNote
      }
    } catch (error) {
      console.error("Error fetching fresh note data:", error)
      // Show error message to user
      const errorMessage = document.createElement("div")
      errorMessage.className = "fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50"
      errorMessage.textContent = "Failed to sync changes. Please refresh the page."
      document.body.appendChild(errorMessage)
      setTimeout(() => {
        if (document.body.contains(errorMessage)) {
          document.body.removeChild(errorMessage)
        }
      }, 5000)
    }
    return null
  }

  const openFullScreenNote = async (note) => {
    // Fetch fresh data before opening fullscreen
    const freshNote = await fetchFreshNoteData(note._id)
    setFullScreenNote(freshNote || note)
    setShowOptionsForPage(null)
  }

  const closeFullScreenNote = async () => {
    if (fullScreenNote) {
      try {
        // Show loading state while syncing
        setIsLoading(true)
        // Fetch fresh data and update all states before closing
        const freshNote = await fetchFreshNoteData(fullScreenNote._id)
        if (freshNote) {
          // Update the notes array with fresh data
          setNotes((prevNotes) => prevNotes.map((note) => (note._id === freshNote._id ? freshNote : note)))
          // Re-organize notes with fresh data
          const allNotes = await getAllnotes(localStorage.getItem("classRoomId"), localStorage.getItem("studentId"))
          const notesArray = allNotes.body.data
          setNotes([...notesArray])
          organizeNotes([...notesArray])
        }
      } catch (error) {
        console.error("Error syncing data:", error)
      } finally {
        setIsLoading(false)
      }
    }

    // Close all fullscreen modes and return to main notes page
    setFullScreenNote(null)
    setShowOptionsForPage(null)
    setExpandedFolder(null) // Reset expanded folder to show clean main page
    // Clear any search or filters to show all notes
    setSearchTerm("")
    setActiveFilter("all")
    // Ensure body scroll is restored
    document.body.style.overflow = "auto"
  }

  const openExcalidrawFullscreen = (content, noteId, pageIndex, side) => {
    setExcalidrawFullscreen({
      ...content,
      _noteId: noteId,
      _pageIndex: pageIndex,
      _side: side,
    })
  }

  const closeExcalidrawFullscreen = async () => {
    if (excalidrawFullscreen) {
      // Sync data before closing
      await fetchFreshNoteData(excalidrawFullscreen._noteId)
    }
    setExcalidrawFullscreen(null)
  }

  const handleDeleteNote = async (noteId) => {
    setIsDeleting(true)
    try {
      const deleteData = {
        request: "delete-Note",
        noteId: noteId,
      }

      const response = await postAws("https://api-dev.aurait.ai/notes/updateNotes", "POST", deleteData)

      setNotes(notes.filter((note) => note._id !== noteId))

      const updatedOrganizedNotes = {}
      Object.entries(organizedNotes).forEach(([folder, folderNotes]) => {
        const filteredNotes = folderNotes.filter((note) => note._id !== noteId)
        if (filteredNotes.length > 0) {
          updatedOrganizedNotes[folder] = filteredNotes
        }
      })
      setOrganizedNotes(updatedOrganizedNotes)

      if (favoriteNotes.includes(noteId)) {
        setFavoriteNotes(favoriteNotes.filter((id) => id !== noteId))
      }

      if (fullScreenNote && fullScreenNote._id === noteId) {
        setFullScreenNote(null)
      }

      setIsDeleteModalOpen(false)
      setDeleteTarget(null)
    } catch (err) {
      console.error("Error deleting note:", err)
      setError("Failed to delete note. Please try again.")
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteFolder = async (folderName) => {
    setIsDeleting(true)
    try {
      const deleteData = {
        request: "delete-NoteFolder",
        noteFolder: folderName,
        userId: localStorage.getItem("studentId"),
      }

      const response = await postAws("https://api-dev.aurait.ai/notes/updateNotes", "POST", deleteData)

      const notesInFolder = organizedNotes[folderName] || []
      const noteIdsInFolder = notesInFolder.map((note) => note._id)

      setNotes(notes.filter((note) => !noteIdsInFolder.includes(note._id)))

      const { [folderName]: _, ...restFolders } = organizedNotes
      setOrganizedNotes(restFolders)

      if (noteIdsInFolder.some((id) => favoriteNotes.includes(id))) {
        setFavoriteNotes(favoriteNotes.filter((id) => !noteIdsInFolder.includes(id)))
      }

      if (fullScreenNote && fullScreenNote.noteFolder === folderName) {
        setFullScreenNote(null)
      }

      if (expandedFolder === folderName) {
        setExpandedFolder(null)
      }

      setIsDeleteModalOpen(false)
      setDeleteTarget(null)
    } catch (err) {
      console.error("Error deleting folder:", err)
      setError("Failed to delete folder. Please try again.")
    } finally {
      setIsDeleting(false)
    }
  }

  const openDeleteModal = (target) => {
    setDeleteTarget(target)
    setIsDeleteModalOpen(true)
  }

  const renderNoteContent = (content, pageIndex, side, noteId) => {
    if (!content) return null

    const { noteType, noteData } = content

    if (!noteType) return null

    const lowerType = noteType.toLowerCase()

    const toggleContentFullscreen = (content, noteId, pageIndex, side) => {
      setFullscreenContent({
        ...content,
        _noteId: noteId,
        _pageIndex: pageIndex,
        _side: side,
      })
    }

    return (
      <div className="w-full h-full overflow-hidden relative">
        {lowerType === "flashcard" ? (
          <Flashcard front={noteData?.front} back={noteData?.back} />
        ) : lowerType === "manual" ? (
          <div
            className="bg-white rounded-lg w-full h-full cursor-pointer"
            onClick={() => openExcalidrawFullscreen(content, noteId, pageIndex, side)}
          >
            <div className="w-full h-full flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all">
              <div className="text-center">
                <PenTool size={48} className="text-gray-400 mx-auto mb-2" />
                <p className="text-gray-600 font-medium">Click to open drawing</p>
                <p className="text-gray-400 text-sm">Opens in fullscreen</p>
              </div>
            </div>
          </div>
        ) : lowerType === "chat" ? (
          <div className="p-4 bg-white rounded-lg overflow-auto max-h-full">{renderLatex(noteData)}</div>
        ) : lowerType === "flowchart" ? (
          <div className="w-full h-full overflow-auto">
            <FlowChart message={{ flowchart: noteData }} />
          </div>
        ) : lowerType === "mnemonic" ? (
          <div className="w-full h-full overflow-auto">
            <MnemonicDevice message={{ mnemonicDevice: noteData }} />
          </div>
        ) : lowerType === "mindmap" ? (
          <div className="w-full h-full overflow-auto">
            <MindMap message={{ mindmap: noteData }} />
          </div>
        ) : lowerType === "none" ? (
          <div className="flex items-center justify-center h-full">
            <button className="p-4 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors">
              <Plus size={24} className="text-gray-500" />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            <Info size={20} className="mr-2" />
            <span>Unsupported note type: {noteType}</span>
          </div>
        )}

        {/* Only show fullscreen button for non-manual note types */}
        {lowerType !== "manual" && lowerType !== "none" && (
          <button
            className="absolute top-2 right-2 p-1.5 bg-white/80 hover:bg-white rounded-full shadow-sm text-gray-600 hover:text-blue-600 transition-colors z-10"
            title="Fullscreen"
            onClick={(e) => {
              e.stopPropagation()
              toggleContentFullscreen(content, noteId, pageIndex, side)
            }}
          >
            <ArrowUpRight size={16} />
          </button>
        )}
      </div>
    )
  }

  const handleAddNewContentType = async (pageIndex, side, contentType, infoSide) => {
    if (!fullScreenNote) return

    if (contentType === "manual") {
      const updatedNote = { ...fullScreenNote }
      const page = { ...updatedNote.noteContents[pageIndex] }
      const refId = uuidv4()

      const addInfoData = {
        body: {
          noteId: fullScreenNote._id,
          pageIndex,
          updateData: {
            noteType: contentType,
            noteData: contentType === "manual" ? refId : "new note",
          },
        },
        request: "add-Info-page",
      }

      const updateData = await postAws("https://api-dev.aurait.ai/notes/updateNotes", "POST", addInfoData)

      page[side] = {
        noteType: contentType,
        noteData: contentType === "manual" ? refId : "new note",
      }

      updatedNote.noteContents[pageIndex] = page
      setFullScreenNote(updatedNote)
      setShowOptionsForPage(null)
    }

    if (contentType === "flashcard") {
      const updatedNote = { ...fullScreenNote }
      const page = { ...updatedNote.noteContents[pageIndex] }

      const addInfoData = {
        body: {
          noteId: fullScreenNote._id,
          pageIndex,
          updateData: JSON.stringify(infoSide),
          noteType: contentType,
        },
        request: "add-Info-page",
      }

      const updateData = await postAws("https://api-dev.aurait.ai/notes/updateNotes", "POST", addInfoData)
      const aiNoteData = JSON.parse(updateData.aiData)

      page[side] = {
        noteType: contentType,
        noteData: { front: aiNoteData.front, back: aiNoteData.back },
      }

      updatedNote.noteContents[pageIndex] = page
      setFullScreenNote(updatedNote)
      setShowOptionsForPage(null)
    }

    if (contentType === "mnemonics") {
      const updatedNote = { ...fullScreenNote }
      const page = { ...updatedNote.noteContents[pageIndex] }

      const addInfoData = {
        body: {
          noteId: fullScreenNote._id,
          pageIndex,
          updateData: JSON.stringify(infoSide),
          noteType: "mnemonic",
        },
        request: "add-Info-page",
      }

      const updateData = await postAws("https://api-dev.aurait.ai/notes/updateNotes", "POST", addInfoData)
      const aiNoteData = JSON.parse(updateData.aiData)

      page[side] = {
        noteType: contentType,
        noteData: JSON.parse(aiNoteData),
      }

      updatedNote.noteContents[pageIndex] = page
      setFullScreenNote(updatedNote)
      setShowOptionsForPage(null)
    }

    if (contentType === "text") {
      const updatedNote = { ...fullScreenNote }
      const page = { ...updatedNote.noteContents[pageIndex] }

      const addInfoData = {
        body: {
          noteId: fullScreenNote._id,
          pageIndex,
          updateData: JSON.stringify(infoSide),
          noteType: "chat",
        },
        request: "add-Info-page",
      }

      const updateData = await postAws("https://api-dev.aurait.ai/notes/updateNotes", "POST", addInfoData)

      page[side] = {
        noteType: "chat",
        noteData: updateData.aiData,
      }

      updatedNote.noteContents[pageIndex] = page
      setFullScreenNote(updatedNote)
      setShowOptionsForPage(null)
    }
  }

  const handleAddNewPage = async () => {
    if (!fullScreenNote) return

    const refId = uuidv4()
    const updatedNote = { ...fullScreenNote }

    const addInfoData = {
      body: {
        noteId: fullScreenNote._id,
        updateData: {
          aSide: { noteType: "manual", noteData: refId },
          bSide: { noteType: "NONE", noteData: null },
        },
      },
      request: "add-Page",
    }

    const updateData = await postAws("https://api-dev.aurait.ai/notes/updateNotes", "POST", addInfoData)

    updatedNote.noteContents.push({
      aSide: { noteType: "manual", noteData: refId },
      bSide: { noteType: "NONE", noteData: null },
    })

    setFullScreenNote(updatedNote)

    setTimeout(() => {
      const pagesContainer = document.getElementById("note-pages-container")
      if (pagesContainer) {
        pagesContainer.scrollTop = pagesContainer.scrollHeight
      }
    }, 100)
  }

  const renderOptionsMenu = (pageIndex, side, infoSide) => {
    if (showOptionsForPage !== `${pageIndex}-${side}`) return null

    return (
      <div className="options-menu absolute bottom-14 right-4 bg-white rounded-lg shadow-lg p-2 z-50 border border-gray-200">
        <div className="flex flex-col gap-1">
          <button
            onClick={() => handleAddNewContentType(pageIndex, side, "manual")}
            className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 rounded-md text-sm"
          >
            <PenTool size={16} className="text-purple-500" />
            <span>Manual</span>
          </button>
          <button
            onClick={() => handleAddNewContentType(pageIndex, side, "flashcard", infoSide)}
            className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 rounded-md text-sm"
          >
            <Bot size={16} className="text-blue-500" />
            <span>Flashcard</span>
          </button>
          <button
            onClick={() => handleAddNewContentType(pageIndex, side, "mnemonics", infoSide)}
            className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 rounded-md text-sm"
          >
            <Bot size={16} className="text-blue-500" />
            <span>Mnemonics</span>
          </button>
          <button
            onClick={() => handleAddNewContentType(pageIndex, side, "text", infoSide)}
            className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 rounded-md text-sm"
          >
            <Bot size={16} className="text-blue-500" />
            <span>Text</span>
          </button>
        </div>
      </div>
    )
  }

  const renderNotePage = (page, index) => {
    const { aSide, bSide } = page

    if (bSide.noteType === "NONE") {
      return (
        <div className="h-[500px] w-full bg-gray-50 rounded-lg p-4 relative mb-6 flex-shrink-0 snap-center">
          <div className="w-full h-full relative">
            {renderNoteContent(aSide, index, "aSide", fullScreenNote?._id)}
            <div className="absolute bottom-4 right-4 z-50">
              <button
                className="p-2 bg-blue-500 rounded-full text-white hover:bg-blue-600 transition-colors shadow-md"
                title="Add content"
                onClick={() => setShowOptionsForPage(`${index}-bSide`)}
              >
                <Plus size={20} />
              </button>
              {renderOptionsMenu(index, "bSide", aSide)}
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="h-[500px] min-w-full flex flex-col md:flex-row gap-4 relative mb-6 flex-shrink-0 snap-center">
        <div className="flex-1 bg-gray-50 rounded-lg p-4 relative">
          {renderNoteContent(aSide, index, "aSide", fullScreenNote?._id)}
        </div>
        <div className="flex-1 bg-gray-50 rounded-lg p-4 relative">
          {renderNoteContent(bSide, index, "bSide", fullScreenNote?._id)}
          {(aSide.noteType === "NONE" || bSide.noteType === "NONE") && (
            <div className="absolute bottom-4 right-4 z-50">
              <button
                className="p-2 bg-blue-500 rounded-full text-white hover:bg-blue-600 transition-colors shadow-md"
                title="Add new page"
                onClick={() => setShowOptionsForPage(`${index}-bSide`)}
              >
                <Plus size={20} />
              </button>
              {renderOptionsMenu(index, "bSide", aSide)}
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderNoteCard = (note) => {
    const firstPage = note?.noteContents?.[0] || {
      aSide: { noteType: "manual", noteData: null },
      bSide: { noteType: "NONE", noteData: null },
    }

    return (
      <div
        key={note._id}
        className={`bg-white rounded-xl p-3 sm:p-4 border border-gray-100 transition-all duration-300 ease-in-out hover:shadow-md ${
          favoriteNotes.includes(note._id) ? "ring-2 ring-blue-200" : ""
        }`}
      >
        <div className="flex justify-between items-start mb-2 sm:mb-3">
          <h3 className="text-base sm:text-lg font-bold text-gray-800 line-clamp-1">
            {note.noteTitle || "Untitled Note"}
          </h3>
          <div className="flex gap-1">
            <button
              className="p-1 sm:p-1.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-gray-100 transition-colors"
              title="Delete note"
              onClick={() => openDeleteModal({ type: "note", id: note._id, title: note.noteTitle })}
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
          <div className="flex items-center bg-gray-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium text-gray-600">
            {getNoteTypeIcon(firstPage.aSide?.noteType)}
            <span className="ml-1 sm:ml-1.5">{formatNoteType(firstPage.aSide?.noteType)}</span>
          </div>
          <div className="text-xs text-gray-500">{formatDate(note.createdAt || new Date())}</div>
          <div className="text-xs text-gray-500">{(note.noteContents && note.noteContents.length) || 0} pages</div>
        </div>

        <div className="bg-gradient-to-r from-gray-50 to-white p-2 sm:p-4 rounded-lg border border-gray-200 h-32 sm:h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:shadow-inner transition-all duration-300 ease-in-out">
          {renderNoteContent(firstPage.aSide, 0, "aSide", note._id)}
        </div>

        <div className="mt-2 sm:mt-3 flex justify-end">
          <button
            onClick={() => openFullScreenNote(note)}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center transition-colors"
          >
            <span>View full note</span>
            <ArrowUpRight size={14} className="ml-1" />
          </button>
        </div>
      </div>
    )
  }

  const renderNoteListItem = (note) => {
    const firstPage = note?.noteContents?.[0] || {
      aSide: { noteType: "manual", noteData: null },
      bSide: { noteType: "NONE", noteData: null },
    }

    return (
      <div
        key={note._id}
        className={`bg-white rounded-xl p-3 sm:p-4 border border-gray-100 transition-all duration-300 ease-in-out hover:shadow-md cursor-pointer ${
          favoriteNotes.includes(note._id) ? "ring-2 ring-blue-200" : ""
        }`}
        onClick={() => openFullScreenNote(note)}
      >
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-0">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex-shrink-0">{getNoteTypeIcon(firstPage.aSide?.noteType)}</div>
            <div>
              <h3 className="text-base sm:text-lg font-bold text-gray-800 line-clamp-1">
                {note.noteTitle || "Untitled Note"}
              </h3>
              <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5 sm:mt-1">
                <span>{formatNoteType(firstPage.aSide?.noteType)}</span>
                <span>•</span>
                <span>{formatDate(note.createdAt || new Date())}</span>
                <span>•</span>
                <span>{(note.noteContents && note.noteContents.length) || 0} pages</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 mt-1 sm:mt-0 justify-end">
            <button
              className="p-1 sm:p-1.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-gray-100 transition-colors"
              title="Delete note"
              onClick={(e) => {
                e.stopPropagation()
                openDeleteModal({ type: "note", id: note._id, title: note.noteTitle })
              }}
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      </div>
    )
  }

  const renderFolderContent = (noteFolder, folderNotes) => {
    const sortedNotes = sortNotes(folderNotes)

    return (
      <div className="bg-white rounded-xl shadow-md overflow-hidden mb-4 sm:mb-6 border border-gray-100 animate-fade-in">
        <div className="p-3 sm:p-4">
          {viewMode === "grid" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {sortedNotes.length > 0 ? (
                sortedNotes.map((note) => renderNoteCard(note))
              ) : (
                <div className="col-span-full flex flex-col items-center justify-center py-6 sm:py-10 text-center">
                  <div className="bg-gray-100 p-3 sm:p-4 rounded-full mb-3 sm:mb-4">
                    <Search className="text-gray-400" size={20} />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-1 sm:mb-2">
                    No matching notes found
                  </h3>
                  <p className="text-sm text-gray-500 max-w-md">
                    Try adjusting your search terms or filters to find what you're looking for.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-3">
              {sortedNotes.length > 0 ? (
                sortedNotes.map((note) => renderNoteListItem(note))
              ) : (
                <div className="flex flex-col items-center justify-center py-6 sm:py-10 text-center">
                  <div className="bg-gray-100 p-3 sm:p-4 rounded-full mb-3 sm:mb-4">
                    <Search className="text-gray-400" size={20} />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-1 sm:mb-2">
                    No matching notes found
                  </h3>
                  <p className="text-sm text-gray-500 max-w-md">
                    Try adjusting your search terms or filters to find what you're looking for.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  const formatNoteType = (type) => {
    if (!type) return "Unknown"
    const lowerType = type.toLowerCase()
    switch (lowerType) {
      case "flashcard":
        return "Flashcard"
      case "manual":
        return "Manual"
      case "chat":
        return "Chat"
      case "flowchart":
        return "Flowchart"
      case "mindmap":
        return "Mind Map"
      case "none":
        return "Empty"
      default:
        return type
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date)
  }

  const renderFolderRows = () => {
    const filteredNotes = getFilteredNotes()
    const folders = Object.entries(filteredNotes)

    if (folders.length === 0) {
      return (
        <div className="bg-white rounded-xl shadow-md p-6 sm:p-8 text-center">
          <div className="bg-blue-100 p-4 sm:p-6 rounded-full inline-flex mb-3 sm:mb-4">
            <Search className="text-blue-600" size={24} />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2 sm:mb-3">No notes found</h2>
          <p className="text-sm sm:text-base text-gray-600 max-w-md mx-auto mb-4 sm:mb-6">
            {searchTerm
              ? "No notes match your search criteria. Try adjusting your search terms or filters."
              : "You don't have any notes yet. Create your first note to get started."}
          </p>
        </div>
      )
    }

    return (
      <div className="space-y-4 sm:space-y-6">
        {folders.map(([noteFolder, folderNotes]) => (
          <div key={noteFolder} className="animate-fade-in">
            <button
              onClick={() => toggleFolder(noteFolder)}
              className={`w-full bg-white rounded-xl shadow-sm p-3 sm:p-4 flex items-center justify-between hover:shadow-md transition-all duration-300 ease-in-out ${
                expandedFolder === noteFolder
                  ? "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 border"
                  : "border border-gray-100"
              }`}
            >
              {expandedFolder === noteFolder ? (
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center">
                    <FolderOpen className="text-blue-600 mr-2 sm:mr-3" size={20} />
                    <div>
                      <span className="text-base sm:text-lg font-bold text-gray-800">{noteFolder}</span>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{folderNotes.length} notes</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Add create note button for this folder */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setNewNoteData({ ...newNoteData, folderName: noteFolder })
                        setIsAddNoteModalOpen(true)
                      }}
                      className="p-1 sm:p-1.5 rounded-full text-blue-500 hover:text-blue-700 hover:bg-blue-50 transition-colors"
                      title="Add note to this folder"
                    >
                      <Plus size={16} />
                    </button>
                    {/* Layout controls */}
                    <div className="flex bg-white rounded-lg shadow-sm p-1 mr-1 sm:mr-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setViewMode("grid")
                        }}
                        className={`p-1 sm:p-1.5 rounded ${
                          viewMode === "grid" ? "bg-blue-100 text-blue-600" : "text-gray-500 hover:text-blue-600"
                        }`}
                        title="Grid view"
                      >
                        <Grid size={16} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setViewMode("list")
                        }}
                        className={`p-1 sm:p-1.5 rounded ${
                          viewMode === "list" ? "bg-blue-100 text-blue-600" : "text-gray-500 hover:text-blue-600"
                        }`}
                        title="List view"
                      >
                        <List size={16} />
                      </button>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setSortOrder((prev) => (prev === "newest" ? "oldest" : "newest"))
                      }}
                      className="text-gray-500 hover:text-blue-600 p-1.5 sm:p-2 rounded-full hover:bg-blue-50 transition-colors"
                      title={sortOrder === "newest" ? "Sort by oldest" : "Sort by newest"}
                    >
                      <ArrowUpDown size={16} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        openDeleteModal({ type: "folder", name: noteFolder })
                      }}
                      className="p-1 sm:p-1.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-gray-100 transition-colors"
                      title="Delete folder"
                    >
                      <Trash2 size={16} />
                    </button>
                    <span className="p-1 sm:p-1.5 rounded-full bg-blue-100 text-blue-600">
                      <ChevronDown size={18} />
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center">
                    <Folder className="text-blue-500 mr-2 sm:mr-3" size={20} />
                    <div>
                      <span className="text-base sm:text-lg font-bold text-gray-800">{noteFolder}</span>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{folderNotes.length} notes</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        openDeleteModal({ type: "folder", name: noteFolder })
                      }}
                      className="p-1 sm:p-1.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-gray-100 transition-colors"
                      title="Delete folder"
                    >
                      <Trash2 size={16} />
                    </button>
                    <span className="p-1 sm:p-1.5 rounded-full text-gray-400">
                      <ChevronRight size={18} />
                    </span>
                  </div>
                </div>
              )}
            </button>
            {expandedFolder === noteFolder && (
              <div className="mt-3 sm:mt-4">{renderFolderContent(noteFolder, folderNotes)}</div>
            )}
          </div>
        ))}
      </div>
    )
  }

  const renderExcalidrawFullscreen = () => {
    if (!excalidrawFullscreen) return null

    return (
      <div className="fixed inset-0 bg-white z-[60] flex flex-col">
        <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-800">Drawing Editor</h3>
          <button
            onClick={closeExcalidrawFullscreen}
            className="p-2 rounded-full text-gray-600 hover:bg-gray-100"
            title="Close drawing editor"
          >
            <XCircle size={20} />
          </button>
        </div>
        <div className="flex-1 p-4 overflow-hidden">
          <div className="w-full h-full bg-white rounded-lg relative">
            {/* Loading overlay */}
            {loadingStates[excalidrawFullscreen.noteData] && (
              <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-30">
                <div className="flex flex-col items-center gap-4 text-blue-600 text-lg">
                  <Loader className="w-8 h-8 animate-spin" />
                  <span>Loading drawing...</span>
                  <div className="text-sm text-gray-500">Please wait while we fetch your drawing</div>
                </div>
              </div>
            )}
            <Excalidraw
              viewModeEnabled={false}
              zenModeEnabled={false}
              gridModeEnabled={true}
              theme="light"
              className="w-full h-full"
              ref={(api) => {
                if (api && excalidrawFullscreen.noteData) {
                  const refId =
                    typeof excalidrawFullscreen.noteData === "string" && excalidrawFullscreen.noteData.includes("/")
                      ? excalidrawFullscreen.noteData.split("/").pop().replace(".json", "")
                      : excalidrawFullscreen.noteData
                  excalidrawRefs.current[refId] = api
                  if (
                    typeof excalidrawFullscreen.noteData === "string" &&
                    excalidrawFullscreen.noteData.includes("/")
                  ) {
                    setTimeout(() => {
                      loadExcalidrawNote(excalidrawFullscreen.noteData, refId)
                    }, 100)
                  }
                }
              }}
            />
            {/* Save button */}
            <div className="absolute top-4 left-4 z-10">
              <button
                onClick={() => {
                  const refId =
                    typeof excalidrawFullscreen.noteData === "string" && excalidrawFullscreen.noteData.includes("/")
                      ? excalidrawFullscreen.noteData.split("/").pop().replace(".json", "")
                      : excalidrawFullscreen.noteData
                  const noteTitle =
                    fullScreenNote?.noteTitle ||
                    notes.find((note) => note._id === excalidrawFullscreen._noteId)?.noteTitle ||
                    "drawing"
                  saveExcalidrawNote(
                    excalidrawFullscreen._noteId,
                    excalidrawFullscreen._pageIndex,
                    excalidrawFullscreen._side,
                    refId,
                    noteTitle,
                  )
                }}
                disabled={savingStates[excalidrawFullscreen.noteData] || loadingStates[excalidrawFullscreen.noteData]}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Save drawing"
              >
                {savingStates[excalidrawFullscreen.noteData] ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : loadingStates[excalidrawFullscreen.noteData] ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>Loading...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Save Drawing</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderFullscreenContent = () => {
    if (!fullscreenContent) return null

    return (
      <div className="fixed inset-0 bg-white z-[60] flex flex-col">
        <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-800">{fullscreenContent.noteType}</h3>
          <button
            onClick={() => setFullscreenContent(null)}
            className="p-2 rounded-full text-gray-600 hover:bg-gray-100"
          >
            <XCircle size={20} />
          </button>
        </div>
        <div className="flex-1 p-4 overflow-auto">
          <div className="w-full h-full">
            {fullscreenContent.noteType?.toLowerCase() === "flashcard" ? (
              <Flashcard front={fullscreenContent?.noteData?.front} back={fullscreenContent?.noteData?.back} />
            ) : fullscreenContent.noteType?.toLowerCase() === "chat" ? (
              <div className="p-4 bg-white rounded-lg max-w-4xl mx-auto">{renderLatex(fullscreenContent.noteData)}</div>
            ) : fullscreenContent.noteType?.toLowerCase() === "flowchart" ? (
              <div className="w-full h-full min-h-[80vh]">
                <FlowChart message={{ flowchart: fullscreenContent.noteData }} />
              </div>
            ) : fullscreenContent.noteType?.toLowerCase() === "mnemonic" ? (
              <div className="w-full h-full min-h-[80vh]">
                <MnemonicDevice message={{ mnemonicDevice: fullscreenContent.noteData }} />
              </div>
            ) : fullscreenContent.noteType?.toLowerCase() === "mindmap" ? (
              <div className="w-full h-full min-h-[80vh]">
                <MindMap message={{ mindmap: fullscreenContent.noteData }} />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <Info size={20} className="mr-2" />
                <span>Unsupported note type: {fullscreenContent.noteType}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderFullScreenNote = () => {
    if (!fullScreenNote) return null

    const totalPages = fullScreenNote.noteContents?.length || 0

    return (
      <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
        <div className="max-w-full mx-auto px-3 sm:px-4 py-4 sm:py-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <button
              onClick={closeFullScreenNote}
              className="flex items-center text-gray-600 hover:text-blue-600 transition-colors"
            >
              <ArrowLeft size={18} className="mr-1 sm:mr-2" />
              <span className="text-sm sm:text-base">Back to notes</span>
            </button>
            <div className="flex items-center gap-1 sm:gap-2">
              <button
                className="p-1.5 sm:p-2 rounded-full text-gray-400 hover:text-red-500 hover:bg-gray-100 transition-colors"
                title="Delete note"
                onClick={() =>
                  openDeleteModal({ type: "note", id: fullScreenNote._id, title: fullScreenNote.noteTitle })
                }
              >
                <Trash2 size={18} />
              </button>
              <button
                onClick={closeFullScreenNote}
                className="p-1.5 sm:p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors ml-1 sm:ml-2"
                title="Close and return to notes"
              >
                <XCircle size={18} />
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
            <div className="p-4 sm:p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">{fullScreenNote.noteTitle}</h1>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <div className="flex items-center bg-white bg-opacity-70 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium text-gray-700 shadow-sm">
                  <FolderOpen size={14} className="text-blue-500 mr-1 sm:mr-1.5" />
                  <span>{fullScreenNote.noteFolder || "Uncategorized"}</span>
                </div>
                <div className="flex items-center bg-white bg-opacity-70 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium text-gray-700 shadow-sm">
                  <Calendar size={14} className="text-gray-500 mr-1 sm:mr-1.5" />
                  <span>{formatDate(fullScreenNote.createdAt || new Date())}</span>
                </div>
                <div className="flex items-center bg-white bg-opacity-70 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium text-gray-700 shadow-sm">
                  <span>{totalPages} pages</span>
                </div>
              </div>
            </div>

            <div
              id="note-pages-container"
              className="p-4 sm:p-6 md:p-8 bg-white overflow-y-auto max-h-[70vh] flex flex-col items-center w-full"
            >
              {(fullScreenNote.noteContents || []).map((page, index) => (
                <div key={index} className="w-full">
                  {renderNotePage(page, index)}
                </div>
              ))}
              <div className="mb-8 mt-2">
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                  onClick={handleAddNewPage}
                >
                  <Plus size={18} className="mr-2" />
                  Add New Page
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderDeleteModal = () => {
    if (!isDeleteModalOpen || !deleteTarget) return null

    const isFolder = deleteTarget.type === "folder"
    const title = isFolder ? deleteTarget.name : deleteTarget.title

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 animate-fade-in">
          <div className="flex items-center mb-4">
            <div className="bg-red-100 p-3 rounded-full mr-4">
              <Trash2 className="text-red-500" size={24} />
            </div>
            <h2 className="text-xl font-bold text-gray-800">{isFolder ? "Delete Folder" : "Delete Note"}</h2>
          </div>
          <p className="text-gray-600 mb-6">
            {isFolder
              ? `Are you sure you want to delete the folder "${title}" and all its notes? This action cannot be undone.`
              : `Are you sure you want to delete the note "${title}"? This action cannot be undone.`}
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button
              onClick={() => (isFolder ? handleDeleteFolder(deleteTarget.name) : handleDeleteNote(deleteTarget.id))}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader size={16} className="animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 size={16} className="mr-2" />
                  Delete {isFolder ? "Folder" : "Note"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    )
  }

  const filterOptions = [
    { id: "all", label: "All Notes", icon: <FileText size={16} /> },
    { id: "flashcard", label: "Flashcards", icon: <BookOpen size={16} /> },
    { id: "manual", label: "Manual Notes", icon: <PenTool size={16} /> },
    { id: "chat", label: "Chat", icon: <MessageCircle size={16} /> },
    { id: "flowchart", label: "Flowcharts", icon: <GitBranch size={16} /> },
    { id: "mindmap", label: "Mind Maps", icon: <Network size={16} /> },
  ]

  const handleCreateNote = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    try {
      const refId = uuidv4()
      const newNote = {
        classRoomId: localStorage.getItem("classRoomId"),
        userId: localStorage.getItem("studentId"),
        userType: "student",
        refId: `${newNoteData?.topic}_${refId}`,
        noteTitle: newNoteData?.topic,
        noteFolder: newNoteData?.folderName || "Uncategorized",
        noteContents: [
          {
            aSide: { noteType: "manual", noteData: refId },
            bSide: { noteType: "NONE", noteData: null },
          },
        ],
      }

      const aiData = await postAws("https://api-dev.aurait.ai/notes", "POST", newNote)

      const createdNote = {
        ...aiData.data,
        noteContents: aiData.data.noteContents || [
          {
            aSide: { noteType: "manual", noteData: refId },
            bSide: { noteType: "NONE", noteData: null },
          },
        ],
      }

      setNotes((prevNotes) => [...prevNotes, createdNote])

      const updatedOrganized = { ...organizedNotes }
      const noteFolder = newNote.noteFolder
      if (!updatedOrganized[noteFolder]) {
        updatedOrganized[noteFolder] = []
      }
      updatedOrganized[noteFolder].push(createdNote)
      setOrganizedNotes(updatedOrganized)

      setExpandedFolder(noteFolder)
      setIsAddNoteModalOpen(false)
      setNewNoteData({ folderName: "", topic: "" })

      setTimeout(() => {
        openFullScreenNote(createdNote)
      }, 100)
    } catch (error) {
      console.error("Error creating note:", error)
      setError("Failed to create note. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const getFilterLabel = () => {
    switch (activeFilter) {
      case "flashcard":
        return "Flashcards"
      case "manual":
        return "Manual"
      case "chat":
        return "Chat"
      case "flowchart":
        return "Flowcharts"
      case "mindmap":
        return "Mind Maps"
      default:
        return "All"
    }
  }

  const totalNotes = notes.length
  const totalFolders = Object.keys(organizedNotes).length
  const flashcardNotes = notes.filter((note) => {
    if (!note?.noteContents || !Array.isArray(note.noteContents)) {
      return false
    }
    return note.noteContents.some((page) => {
      if (!page?.aSide || !page?.bSide) {
        return false
      }
      return (
        (page.aSide.noteType && page.aSide.noteType === "flashcard") ||
        (page.bSide.noteType && page.bSide.noteType === "flashcard")
      )
    })
  }).length

  const manualNotes = notes.filter((note) => {
    if (!note?.noteContents || !Array.isArray(note.noteContents)) {
      return false
    }
    return note.noteContents.some((page) => {
      if (!page?.aSide || !page?.bSide) {
        return false
      }
      return (
        (page.aSide.noteType && page.aSide.noteType === "manual") ||
        (page.bSide.noteType && page.bSide.noteType === "manual")
      )
    })
  }).length

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md text-center max-w-md mx-auto">
          <Loader className="animate-spin text-blue-600 mx-auto mb-3 sm:mb-4" size={36} />
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-1 sm:mb-2">Loading your notes</h2>
          <p className="text-sm sm:text-base text-gray-600">Please wait while we fetch your notes...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md text-center max-w-md mx-auto">
          <AlertCircle className="text-red-500 mx-auto mb-3 sm:mb-4" size={36} />
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-1 sm:mb-2">Something went wrong</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">{error}</p>
          <button
            onClick={() => {
              setNotesFetched(false)
              setError(null)
            }}
            className="px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 -mt-6">
      <div className="container mx-auto px-2 md:px-4 py-0 max-w-7xl">
        <div className="bg-white rounded-xl md:rounded-2xl shadow-md overflow-hidden mt-6 md:mt-8">
          <div className="bg-gradient-to-r from-indigo-500 to-blue-600 px-3 md:px-4 py-3 md:py-4">
            {/* Mobile view */}
            <div className="block lg:hidden">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 mr-1">
                  <div className="bg-white/20 backdrop-blur-sm rounded-md px-1 py-0.5 text-center">
                    <p className="text-white text-[10px] font-bold">{totalNotes}</p>
                    <p className="text-white/80 text-[8px]">Total</p>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-md px-1 py-0.5 text-center">
                    <p className="text-white text-[10px] font-bold">{totalFolders}</p>
                    <p className="text-white/80 text-[8px]">Folders</p>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-md px-1 py-0.5 text-center">
                    <p className="text-white text-[10px] font-bold">{flashcardNotes}</p>
                    <p className="text-white/80 text-[8px]">Flashcards</p>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-md px-1 py-0.5 text-center">
                    <p className="text-white text-[10px] font-bold">{manualNotes}</p>
                    <p className="text-white/80 text-[8px]">Manual</p>
                  </div>
                </div>
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-1.5 pl-7 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-300 ease-in-out bg-white/10 backdrop-blur-sm text-white placeholder-white/70 text-xs"
                  />
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-white/70" size={12} />
                </div>
                <div className="relative">
                  <button
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-white/20 backdrop-blur-sm text-white text-xs font-medium border border-white/30"
                  >
                    <Filter size={12} />
                    <ChevronDown
                      size={10}
                      className={`transition-transform ${showFilterDropdown ? "rotate-180" : ""}`}
                    />
                  </button>
                  {showFilterDropdown && (
                    <div className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg z-10 border border-gray-200 py-1 animate-scale-in">
                      {filterOptions.map(({ id, label, icon }) => (
                        <button
                          key={id}
                          onClick={() => {
                            setActiveFilter(id)
                            setShowFilterDropdown(false)
                          }}
                          className={`w-full px-2 py-1.5 text-xs text-left flex items-center transition-colors hover:bg-gray-50 ${
                            activeFilter === id ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                          }`}
                        >
                          <span className="mr-1.5">{icon}</span>
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setIsAddNoteModalOpen(true)}
                  className="bg-white/20 backdrop-blur-sm text-white px-2 py-1.5 rounded-lg hover:bg-white/30 transition-colors duration-300 flex items-center gap-1 border border-white/30 text-xs font-medium"
                >
                  <Plus size={12} />
                  <span>Create</span>
                </button>
              </div>
            </div>

            {/* Desktop view */}
            <div className="hidden lg:flex lg:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                {[
                  {
                    icon: <FileText className="w-4 h-4 opacity-80" />,
                    label: "Total",
                    value: totalNotes,
                  },
                  {
                    icon: <Folder className="w-4 h-4 opacity-80" />,
                    label: "Folders",
                    value: totalFolders,
                  },
                  {
                    icon: <BookOpen className="w-4 h-4 opacity-80" />,
                    label: "Flashcards",
                    value: flashcardNotes,
                  },
                  {
                    icon: <PenTool className="w-4 h-4 opacity-80" />,
                    label: "Manual",
                    value: manualNotes,
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="inline-flex items-center gap-2 bg-white/10 border border-white/30 backdrop-blur-sm rounded-md px-3 py-1 text-white text-xs font-medium whitespace-nowrap"
                  >
                    {item.icon}
                    <span>
                      {item.label}: <span className="font-bold">{item.value}</span>
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search notes"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-64 px-4 py-2 pl-10 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-300 ease-in-out bg-white/10 backdrop-blur-sm text-white placeholder-white/70 text-sm"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70" size={14} />
                </div>

                <div className="relative">
                  <button
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/20 backdrop-blur-sm text-white text-sm font-medium border border-white/30 hover:bg-white/30 transition-colors"
                  >
                    <Filter size={14} />
                    <span>{getFilterLabel()}</span>
                    <ChevronDown
                      size={14}
                      className={`transition-transform ${showFilterDropdown ? "rotate-180" : ""}`}
                    />
                  </button>
                  {showFilterDropdown && (
                    <div className="absolute right-0 mt-1 w-40 bg-white rounded-md shadow-lg z-10 border border-gray-200 py-1 animate-scale-in">
                      {filterOptions.map(({ id, label, icon }) => (
                        <button
                          key={id}
                          onClick={() => {
                            setActiveFilter(id)
                            setShowFilterDropdown(false)
                          }}
                          className={`w-full px-3 py-2 text-sm text-left flex items-center transition-colors hover:bg-gray-50 ${
                            activeFilter === id ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                          }`}
                        >
                          <span className="mr-2">{icon}</span>
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setIsAddNoteModalOpen(true)}
                  className="bg-white/20 backdrop-blur-sm text-white px-5 py-2 rounded-lg hover:bg-white/30 transition-colors duration-300 flex items-center gap-2 border border-white/30 text-sm font-medium"
                >
                  <Plus size={16} />
                  <span>Create Note</span>
                </button>
              </div>
            </div>
          </div>

          <div className="p-3 md:p-6">
            <div className="space-y-4 sm:space-y-6">{renderFolderRows()}</div>
          </div>
        </div>
      </div>

      {fullScreenNote && renderFullScreenNote()}

      {isAddNoteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 animate-fade-in">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Add New Note</h2>
              <button onClick={() => setIsAddNoteModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateNote}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="folderName" className="block text-sm font-medium text-gray-700 mb-1">
                    Folder Name
                  </label>
                  <input
                    type="text"
                    id="folderName"
                    value={newNoteData.folderName}
                    onChange={(e) => setNewNoteData({ ...newNoteData, folderName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter folder name"
                  />
                </div>
                <div>
                  <label htmlFor="topic" className="block text-sm font-medium text-gray-700 mb-1">
                    Note Topic <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="topic"
                    required
                    value={newNoteData.topic}
                    onChange={(e) => setNewNoteData({ ...newNoteData, topic: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter note topic"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsAddNoteModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
                >
                  {isLoading ? (
                    <>
                      <Loader size={16} className="animate-spin mr-2" />
                      Creating...
                    </>
                  ) : (
                    "Create"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {renderDeleteModal()}
      {excalidrawFullscreen && renderExcalidrawFullscreen()}
      {fullscreenContent && renderFullscreenContent()}

      {Object.values(savingStates).some(Boolean) && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-[80] flex items-center justify-center">
          <div className="bg-white p-6 rounded-xl shadow-lg flex items-center gap-3">
            <Loader className="w-6 h-6 animate-spin text-blue-600" />
            <span className="text-gray-700 font-medium">Saving your drawing...</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default Notes
